import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Mail, Phone, Trash2, Pencil, Eye } from "lucide-react";
import Shell from "@/components/Shell";
import { Card, PageHeader, Btn, Input, Spinner, Empty, IconBtn } from "@/components/ui-kit";
import api from "@/lib/api";
import { useConfirm } from "@/lib/confirm";

const ClientRow = ({ c, onDelete }) => (
  <Card
    data-testid={`client-card-${c.id}`}
    className="px-5 py-4 hover:border-zinc-300 hover:shadow-sm transition-all"
  >
    <div className="flex items-center gap-6">
      {/* Name + contact info */}
      <div className="flex-1 min-w-0">
        <Link to={`/clients/${c.id}`} className="block">
          <h3 className="font-display text-lg font-semibold text-zinc-950 hover:text-zinc-700 transition-colors truncate">
            {c.name}
          </h3>
        </Link>
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-zinc-500">
          {c.email && (
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <Mail className="w-3 h-3 text-zinc-400 shrink-0" />
              <span className="truncate">{c.email}</span>
            </span>
          )}
          {c.phone && (
            <span className="inline-flex items-center gap-1.5">
              <Phone className="w-3 h-3 text-zinc-400" />
              {c.phone}
            </span>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="hidden sm:flex items-center gap-5 shrink-0 text-center">
        <Link
          to={`/projects?client=${c.id}&filter=live`}
          data-testid={`client-live-projects-${c.id}`}
          className="group/stat block"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="font-display text-lg font-bold leading-none">
            <span className="text-zinc-950">{c.project_count}</span>
            {c.live_project_count > 0 && (
              <span className="text-zinc-400 font-medium"> / </span>
            )}
            {c.live_project_count > 0 && (
              <span className="text-blue-600 group-hover/stat:underline">{c.live_project_count}</span>
            )}
          </p>
          <p className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">
            Projects{c.live_project_count > 0 ? " · live" : ""}
          </p>
        </Link>
        <div className="w-px h-7 bg-zinc-100" />
        <Link
          to={`/quotes?client=${c.id}&filter=live`}
          data-testid={`client-live-quotes-${c.id}`}
          className="group/stat block"
          onClick={(e) => e.stopPropagation()}
        >
          <p className="font-display text-lg font-bold leading-none">
            <span className="text-zinc-950">{c.quote_count}</span>
            {c.live_quote_count > 0 && (
              <span className="text-zinc-400 font-medium"> / </span>
            )}
            {c.live_quote_count > 0 && (
              <span className="text-blue-600 group-hover/stat:underline">{c.live_quote_count}</span>
            )}
          </p>
          <p className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">
            Quotes{c.live_quote_count > 0 ? " · live" : ""}
          </p>
        </Link>
        <div className="w-px h-7 bg-zinc-100" />
        <div>
          <p className="font-display text-lg font-bold text-zinc-950 leading-none">{c.contact_count}</p>
          <p className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">Contacts</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 shrink-0">
        <Link to={`/clients/${c.id}`} data-testid={`client-view-${c.id}`}>
          <Btn variant="secondary" size="sm"><Eye className="w-3.5 h-3.5" />View</Btn>
        </Link>
        <Link to={`/clients/${c.id}/edit`} data-testid={`client-edit-${c.id}`}>
          <Btn variant="secondary" size="sm"><Pencil className="w-3.5 h-3.5" />Edit</Btn>
        </Link>
        <IconBtn variant="destructive" onClick={() => onDelete(c.id)} data-testid={`client-delete-${c.id}`}>
          <Trash2 className="w-4 h-4" />
        </IconBtn>
      </div>
    </div>
  </Card>
);

const ClientsList = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/clients");
    setClients(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!(await confirm({ title: "Delete this client?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/clients/${id}`);
    await load();
  };

  const filtered = clients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <Shell>
      <PageHeader
        testId="clients-header"
        title="Clients"
        subtitle="Manage your client relationships"
        actions={
          <Btn onClick={() => navigate("/clients/new")} data-testid="new-client-btn">
            <Plus className="w-4 h-4" />New Client
          </Btn>
        }
      />

      <div className="mb-6 max-w-sm">
        <Input
          data-testid="clients-search"
          placeholder="Search clients..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <Empty
          title="No clients yet"
          description="Add your first client to start tracking projects and quotes."
          action={<Btn onClick={() => navigate("/clients/new")}><Plus className="w-4 h-4" />New Client</Btn>}
        />
      ) : (
        <div className="flex flex-col gap-2.5">
          {filtered.map((c) => <ClientRow key={c.id} c={c} onDelete={del} />)}
        </div>
      )}
    </Shell>
  );
};

export default ClientsList;
